import React from 'react';
import { Link } from 'react-router-dom';
import { SupportLayout } from './SupportLayout';

export const ResponsibleHostingPage: React.FC = () => {
  return (
    <SupportLayout
      title="Responsible hosting"
      subtitle="Local rules, neighbours, taxes. The boring side of hosting that, if you skip, comes back to bite you."
    >
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">Check your building's rules</h2>
      <p className="text-gray-700 mb-3">
        Some Lebanese buildings ban short-term rentals in their bylaws. Before listing, check with your building
        committee or owner's association. A signed permission letter saves you from disputes later.
      </p>
      <p className="text-gray-700">
        Detached houses, chalets, and standalone villas usually have no such rule, but if you're inside a gated
        community, ask the management.
      </p>

      <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">Talk to your neighbours</h2>
      <p className="text-gray-700">
        A 2-minute conversation before your first guest arrives prevents 90% of future complaints. Tell them you'll
        be hosting, give them your number, and ask them to call you (not the police) if anything bothers them.
      </p>

      <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">Taxes</h2>
      <p className="text-gray-700 mb-3">
        Rental income is taxable in Lebanon. As of 2026, short-term rentals are taxed at progressive rates on net
        income. We don't collect or remit on your behalf. You're responsible for declaring earnings.
      </p>
      <p className="text-gray-700">
        We send each host a year-end earnings statement in January, listing every payout. Hand it to your
        accountant.
      </p>

      <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">Insurance</h2>
      <p className="text-gray-700 mb-3">
        DARAK's host protection covers up to $1,000,000 per stay for guest-caused damage. It does <em>not</em>{' '}
        replace your homeowner's insurance. Talk to your insurer about adding a short-term rental rider.
      </p>
      <p className="text-gray-700">
        Things our cover doesn't include: theft by people other than the registered guest, damage from natural
        disasters, and gradual wear and tear.
      </p>

      <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">Safety basics</h2>
      <ul className="space-y-3 text-gray-700">
        <li>Working smoke detector on every floor.</li>
        <li>A fire extinguisher in or near the kitchen.</li>
        <li>A first-aid kit in a clearly labelled spot.</li>
        <li>Emergency contacts (yours, ours, 112) printed and visible.</li>
        <li>No exposed wiring, no loose railings, no broken steps.</li>
      </ul>

      <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">Set clear house rules</h2>
      <p className="text-gray-700 mb-3">
        Spell out exactly what's not allowed: parties, smoking inside, pets, extra guests beyond the booked count.
        Vague rules cause disputes; specific ones prevent them.
      </p>
      <p className="text-gray-700">
        You set these when you create or edit a listing. Guests see and accept them at booking time.
      </p>

      <div className="mt-12 p-6 bg-amber-50 border border-amber-200 rounded-2xl">
        <p className="text-gray-900 font-medium mb-2">When something goes wrong</p>
        <p className="text-gray-700 mb-4">
          A noise complaint, a damaged item, a guest who won't leave: call us before it escalates. We've handled
          all of these before.
        </p>
        <Link
          to="/contact"
          className="inline-block px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors"
        >
          Reach the team
        </Link>
      </div>
    </SupportLayout>
  );
};

export default ResponsibleHostingPage;
